import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { submitQuestionnaire } from "@/lib/api";
import { ChevronRight } from "lucide-react";

// Renders a single Likert-scale questionnaire (PHQ-9, GAD-7, SWLS, PERMA, or one GHQ section)
function LikertSection({ scale, items, prefix, answers, setAnswers }) {
  return (
    <div className="space-y-4">
      {items.map((text, idx) => {
        const key = `${prefix}${idx}`;
        return (
          <Card key={key} className="border-[var(--ha-border)] rounded-2xl bg-white">
            <CardContent className="p-5 sm:p-6">
              <p className="font-semibold text-[var(--ha-text)] leading-snug">
                {idx + 1}. {text}
              </p>
              <RadioGroup
                value={answers[key]?.toString() ?? ""}
                onValueChange={(v) => setAnswers({ ...answers, [key]: Number(v) })}
                className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2"
              >
                {scale.map((opt) => {
                  const val = typeof opt === "object" ? opt.value : opt;
                  const label = typeof opt === "object" ? opt.label : String(opt);
                  const id = `${key}-${val}`;
                  const selected = String(answers[key]) === String(val);
                  return (
                    <label
                      key={id}
                      htmlFor={id}
                      data-testid={`q-${prefix}${idx}-opt-${val}`}
                      className={`cursor-pointer rounded-xl border px-3 py-3 flex items-center gap-3 transition-colors ${selected ? "border-[var(--ha-primary)] bg-[var(--ha-primary)]/5" : "border-[var(--ha-border)] hover:bg-[var(--ha-bg)]"}`}
                    >
                      <RadioGroupItem id={id} value={String(val)} />
                      <span className="text-sm text-[var(--ha-text)]">{label}</span>
                    </label>
                  );
                })}
              </RadioGroup>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function QuestionnaireStep({ questionnaire, onDone, phase = "baseline" }) {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const totalItems = useMemo(() => {
    if (questionnaire.sections) return questionnaire.sections.reduce((s, sec) => s + sec.items.length, 0);
    return questionnaire.items.length;
  }, [questionnaire]);

  const answered = Object.keys(answers).length;
  const allAnswered = answered >= totalItems;

  const handleSubmit = async () => {
    if (!allAnswered) {
      toast.error("Completa todas las preguntas antes de continuar.");
      return;
    }
    setSubmitting(true);
    try {
      // For BHS convert V/F to 1/0 based on hopeless_when
      let payload = { ...answers };
      if (questionnaire.kind === "bhs") {
        payload = {};
        questionnaire.items.forEach((it, idx) => {
          const key = `q${idx}`;
          const chosen = answers[key];
          payload[key] = chosen === it.hopeless_when ? 1 : 0;
        });
      }
      const result = await submitQuestionnaire(questionnaire.kind, payload, phase);
      toast.success(`Puntuación: ${result.score} · ${result.interpretation}`);
      onDone(result);
    } catch {
      toast.error("No pudimos guardar tus respuestas. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[var(--ha-text)]">{questionnaire.title}</h2>
      <p className="mt-3 text-[var(--ha-muted)] leading-relaxed">{questionnaire.intro}</p>

      <div className="mt-8 space-y-8">
        {questionnaire.sections
          ? questionnaire.sections.map((sec, sIdx) => (
              <div key={sIdx}>
                <p className="text-xs uppercase tracking-[0.16em] text-[var(--ha-primary)] font-bold mb-4">{sec.title}</p>
                <LikertSection
                  scale={sec.scale}
                  items={sec.items}
                  prefix={`s${sIdx}q`}
                  answers={answers}
                  setAnswers={setAnswers}
                />
              </div>
            ))
          : questionnaire.kind === "bhs"
          ? (
            <div className="space-y-4">
              {questionnaire.items.map((it, idx) => {
                const key = `q${idx}`;
                return (
                  <Card key={key} className="border-[var(--ha-border)] rounded-2xl bg-white">
                    <CardContent className="p-5 sm:p-6">
                      <p className="font-semibold text-[var(--ha-text)] leading-snug">{idx + 1}. {it.text}</p>
                      <RadioGroup
                        value={answers[key] ?? ""}
                        onValueChange={(v) => setAnswers({ ...answers, [key]: v })}
                        className="mt-4 flex gap-2"
                      >
                        {["V", "F"].map((val) => {
                          const id = `bhs-${idx}-${val}`;
                          const selected = answers[key] === val;
                          return (
                            <label
                              key={id}
                              htmlFor={id}
                              data-testid={`q-bhs-${idx}-${val}`}
                              className={`cursor-pointer rounded-xl border px-4 py-2.5 flex items-center gap-2.5 transition-colors ${selected ? "border-[var(--ha-primary)] bg-[var(--ha-primary)]/5" : "border-[var(--ha-border)] hover:bg-[var(--ha-bg)]"}`}
                            >
                              <RadioGroupItem id={id} value={val} />
                              <Label htmlFor={id} className="text-sm cursor-pointer">{val === "V" ? "Verdadero" : "Falso"}</Label>
                            </label>
                          );
                        })}
                      </RadioGroup>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )
          : (
            <LikertSection
              scale={questionnaire.scale}
              items={questionnaire.items}
              prefix="q"
              answers={answers}
              setAnswers={setAnswers}
            />
          )}
      </div>

      <div className="mt-8 flex items-center justify-between gap-3">
        <span className="text-sm text-[var(--ha-muted)]">{answered}/{totalItems} respondidas</span>
        <Button
          data-testid={`submit-${questionnaire.kind}-btn`}
          onClick={handleSubmit}
          disabled={submitting || !allAnswered}
          className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6"
        >
          Guardar y continuar <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
